import React, { useState, useEffect, useMemo } from 'react';
import { useAuth, checkIonicSession } from '../../contexts/AuthContext';
import appsData from '../../assets/apps.json';
import './AppLauncher.css';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL;
const KIOSK_URL = import.meta.env.VITE_KIOSK_URL;

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'production';

function getEnvironmentKey() {
  if (ENVIRONMENT === 'production') return 'production';
  if (ENVIRONMENT === 'integration-testing') return 'integration-testing';
  return 'testing';
}

function appIsEnabled(app, user) {
  const userApps = user.apps || [];
  const { name, desc } = app;

  if (name === 'Checklist') return userApps.find((x) => x.name === 'Checklist');
  if (name === '1Forms') return userApps.find((x) => x.name === '1Forms');
  if (name === '1FormsQT') return userApps.find((x) => x.name === '1Forms URL');
  if (name === 'IR 2.0') return userApps.find((x) => x.code === 'onerisk-app-ir');
  if (name === 'IR 3.0') return userApps.find((x) => x.code === 'onerisk-app-ims');
  if (name === 'IMS') return userApps.find((x) => x.code === 'onerisk-app-ims');
  if (name.startsWith('1Waiver SLC')) return userApps.find((x) => x.name === '1Waiver');
  if (name === '1Tracker') return userApps.find((x) => x.name === '1Tracker');
  if (name.startsWith('1Waiver KIOSK')) return userApps.find((x) => x.name === '1Waiver');
  if (name.startsWith('Group Manager')) return userApps.find((x) => x.name === 'Group Manager');
  if (name === '1Check') return userApps.find((x) => x.code === 'onerisk-app-ac');
  if (name === '1Check V2') return userApps.find((x) => x.code === 'onerisk-app-ac');
  if (desc.startsWith('1Dispatch Command')) return userApps.find((x) => x.name === 'Dispatch');
  if (desc === '1Dispatch Control') return userApps.find((x) => x.name === 'Dispatch');

  const adminApp = userApps.find((x) => x.code === 'onerisk-app-admin');
  if (adminApp && user.highestRole === 'ROLE_ADMIN') return adminApp;

  return false;
}

/**
 * Map each app to a group key based on name prefix.
 */
function getGroupKey(app) {
  if (app.name.startsWith('1Admin')) return 'admin';
  if (app.name === 'IR 2.0' || app.name === 'IR 3.0' || app.name === 'IMS') return 'incidents';
  if (app.name === 'Dispatch') return 'dispatch';
  if (app.name.startsWith('1Waiver') || app.name.startsWith('Group Manager')) return 'waivers';
  if (app.name === '1Check' || app.name === '1Check V2' || app.name === 'Checklist') return 'checklists';
  if (app.name === '1Tracker') return 'tracker';
  if (app.name === '1Forms' || app.name === '1FormsQT') return 'forms';
  return 'other';
}

const GROUP_CONFIG = {
  admin: {
    label: 'Administration',
    color: '#e0ac08',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    ),
  },
  incidents: {
    label: 'Incident Management',
    color: '#eb445a',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
  },
  dispatch: {
    label: 'Dispatch & Communications',
    color: '#3dc2ff',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="22" />
      </svg>
    ),
  },
  waivers: {
    label: 'Waivers',
    color: '#5260ff',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
        <polyline points="10 9 9 9 8 9" />
      </svg>
    ),
  },
  groups: {
    label: 'Group Management',
    color: '#2dd36f',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
  },
  checklists: {
    label: 'Checklists',
    color: '#f59e0b',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 11 12 14 22 4" />
        <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
      </svg>
    ),
  },
  tracker: {
    label: 'Security Patrol',
    color: '#3171e0',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
  },
  forms: {
    label: 'Forms',
    color: '#2dd36f',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <line x1="3" y1="9" x2="21" y2="9" />
        <line x1="9" y1="21" x2="9" y2="9" />
      </svg>
    ),
  },
  other: {
    label: 'Queues',
    color: '#6c757d',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" />
        <rect x="14" y="3" width="7" height="7" />
        <rect x="3" y="14" width="7" height="7" />
        <rect x="14" y="14" width="7" height="7" />
      </svg>
    ),
  },
};

const GROUP_ORDER = ['admin', 'incidents', 'dispatch', 'waivers', 'other', 'groups', 'checklists', 'tracker', 'forms'];

const BANNER_URL = import.meta.env.BASE_URL + 'banner.json';

const BANNER_STYLES = {
  info: { background: '#e8f4fd', border: '#2196f3', color: '#0d47a1', icon: 'info' },
  warning: { background: '#fff8e1', border: '#ff9800', color: '#e65100', icon: 'warning' },
  critical: { background: '#fde8e8', border: '#f44336', color: '#b71c1c', icon: 'critical' },
};

const AppLauncher = () => {
  const { user, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    const envKey = getEnvironmentKey();
    const filtered = appsData.filter((app) => app[envKey] === true);
    setApps(filtered);

    fetch(BANNER_URL, { cache: 'no-cache' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data || !data.active) return;
        if (data.expires && new Date(data.expires) < new Date()) return;
        setBanner(data);
      })
      .catch(() => {});
  }, []);

  const enabledApps = apps.filter((app) => appIsEnabled(app, user));

  const groupedApps = useMemo(() => {
    const groups = {};
    for (const app of enabledApps) {
      const key = getGroupKey(app);
      if (!groups[key]) groups[key] = [];
      groups[key].push(app);
    }
    return groups;
  }, [enabledApps]);

  const handleLaunchApp = async (app) => {
    const sessionValid = await Promise.race([
      checkIonicSession(),
      new Promise((resolve) => setTimeout(() => resolve(true), 3000)),
    ]);
    if (!sessionValid) {
      sessionStorage.setItem('portalSessionMessage', 'You have been signed out by another 1RISK application. Please log in again.');
      logout();
      return;
    }
    const isAbsoluteUrl = app.path.startsWith('http://') || app.path.startsWith('https://');
    const baseUrl = PORTAL_URL.endsWith('/') ? PORTAL_URL : PORTAL_URL + '/';
    if (app.name === '1Waiver KIOSK 3' || app.name === '1Waiver KIOSK 2026') {
      window.open(baseUrl + app.path + '/index.html', '_blank');
    } else if (app.name === '1Waiver KIOSK 2') {
      window.open(KIOSK_URL, '_blank');
    } else if (isAbsoluteUrl) {
      window.open(app.path, '_self');
    } else {
      window.open(baseUrl + app.path + '/index.html', '_self');
    }
  };

  const bannerStyle = banner ? BANNER_STYLES[banner.type] || BANNER_STYLES.info : null;

  return (
    <div className="app-launcher">
      {banner && bannerStyle && (
        <div
          className="banner"
          style={{
            background: bannerStyle.background,
            borderLeft: `4px solid ${bannerStyle.border}`,
            color: bannerStyle.color,
          }}
        >
          <div className="banner-icon">
            {bannerStyle.icon === 'critical' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            ) : bannerStyle.icon === 'warning' ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            )}
          </div>
          <span className="banner-message">{banner.message}</span>
          {banner.link && (
            <a href={banner.link} target="_blank" rel="noopener noreferrer" className="banner-link">
              Learn more
            </a>
          )}
          <button className="banner-dismiss" onClick={() => setBanner(null)} aria-label="Dismiss">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      )}

      {enabledApps.length === 0 && (
        <div className="no-apps">
          <p>No applications available for your account.</p>
        </div>
      )}

      <div className="app-groups">
        {GROUP_ORDER.filter((key) => groupedApps[key]).map((key) => {
          const config = GROUP_CONFIG[key];
          const groupApps = groupedApps[key];

          return (
            <div className="app-group-card" key={key}>
              <div className="app-group-header">
                <div className="app-group-icon" style={{ color: config.color }}>
                  {config.icon}
                </div>
                <h2 className="app-group-title">{config.label}</h2>
              </div>
              <div className="app-group-items">
                {groupApps.map((app, index) => (
                  <button
                    className="app-item"
                    key={`${app.path}-${index}`}
                    onClick={() => handleLaunchApp(app)}
                  >
                    <div className="app-item-accent" style={{ backgroundColor: config.color }} />
                    <div className="app-item-content">
                      <span className="app-item-name">{app.name}</span>
                      <span className="app-item-desc">{app.desc}</span>
                    </div>
                    <svg className="app-item-arrow" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppLauncher;
